import React from "react";
import {
    Card, CardBody, Container, Row, Col, Table,
    Badge, Button, Spinner
} from "reactstrap";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { sessionEnded } from "../user/SessionEnded";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";
import { adminListShareableSongFlows } from "../../components/helpers/PublicCuratedSongFlowHelpers";

class AdminCuratedSongFlows extends React.Component {
    state = {
        unauthorized: false,
        isLoading: true,
        songFlows: [],
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
                return;
            }
        } catch (e) {
            this.setState({ unauthorized: true });
            return;
        }
        this.loadData();
    }

    async loadData() {
        const data = await adminListShareableSongFlows();
        this.setState({ songFlows: data || [], isLoading: false });
    }

    formatDate(dt) {
        if (!dt) return "—";
        return new Date(dt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    }

    render() {
        if (!sessionStorage.getItem("customer")) return sessionEnded();
        if (this.state.unauthorized) return <Navigate to="/" />;

        const { isLoading, songFlows } = this.state;
        const { navigate } = this.props;

        return (
            <>
                <SeoNoIndex noindex={true} />

                <div className="position-relative">
                    <section className="section section-lg section-shaped pb-250">
                        <div className="shape shape-style-1 shape-default">
                            <span /><span /><span /><span /><span /><span /><span /><span /><span />
                        </div>
                        <Container className="py-lg-md d-flex">
                            <div className="col px-0">
                                <Row>
                                    <Col lg="8">
                                        <h1 className="display-3 text-white">Published Song-Flows</h1>
                                        <p className="lead text-white">
                                            Manage shareable curated song-flows, subscribers, and send published worship plans by email.
                                        </p>
                                    </Col>
                                </Row>
                            </div>
                        </Container>
                        <div className="separator separator-bottom separator-skew">
                            <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                                <polygon className="fill-white" points="2560 0 2560 100 0 100" />
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="section section-lg pt-lg-0 mt--200">
                    <Container>
                        <Row className="justify-content-center">
                            <Col lg="12">
                                <Card className="shadow border-0">
                                    <CardBody className="py-4 px-4">
                                        {isLoading ? (
                                            <div className="text-center py-4">
                                                <Spinner color="primary" />
                                            </div>
                                        ) : songFlows.length === 0 ? (
                                            <div className="text-center py-4 text-muted">
                                                <p>No shareable song-flows found.</p>
                                                <p style={{ fontSize: "0.9rem" }}>
                                                    Mark a saved song-flow as <strong>Shareable curated song-flow</strong> when saving or editing it, then manage it here.
                                                </p>
                                            </div>
                                        ) : (
                                            <Table responsive hover>
                                                <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        <th>Status</th>
                                                        <th>Items</th>
                                                        <th>Subscribers</th>
                                                        <th>Last emailed</th>
                                                        <th>Published</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {songFlows.map(sf => (
                                                        <tr key={sf.id}>
                                                            <td>
                                                                <strong>{sf.name}</strong>
                                                                {sf.notes && (
                                                                    <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                                                                        {sf.notes.length > 60 ? sf.notes.substring(0, 60) + "…" : sf.notes}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {sf.published
                                                                    ? <Badge color="success">Published</Badge>
                                                                    : <Badge color="secondary">Unpublished</Badge>}
                                                            </td>
                                                            <td>{sf.itemCount}</td>
                                                            <td>{sf.subscriberCount}</td>
                                                            <td>{this.formatDate(sf.lastEmailedAt)}</td>
                                                            <td>{this.formatDate(sf.publishedAt)}</td>
                                                            <td>
                                                                <Button
                                                                    size="sm"
                                                                    color="primary"
                                                                    outline
                                                                    onClick={() => navigate(`/admin/curated-song-flows/${sf.id}`)}
                                                                >
                                                                    Manage
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </>
        );
    }
}

function withRouterParamsAndNavigate(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}

export default withRouterParamsAndNavigate(AdminCuratedSongFlows);
